/**
 * FCM Token Management Service
 * 
 * Manages Firebase Cloud Messaging (FCM) tokens for push notifications
 */

import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import Constants from 'expo-constants';

const isValidUuid = (value?: string | null): boolean => {
  if (!value) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
};

/**
 * Get the current FCM/expo push token
 */
export const getPushToken = async (): Promise<string | null> => {
  try {
    // Skip push token on web platform if VAPID key is not configured
    // This prevents errors when running on web without proper push notification setup
    if (Platform.OS === 'web') {
      console.log('[FCM] Skipping push token on web platform');
      return null;
    }

    const { status } = await Notifications.getPermissionsAsync();
    
    if (status !== 'granted') {
      const { status: newStatus } = await Notifications.requestPermissionsAsync();
      if (newStatus !== 'granted') {
        console.warn('[FCM] Notification permission not granted');
        return null;
      }
    }

    // For Expo, we use Expo's push notification token
    // In production with bare workflow, you'd use FCM token
    const projectIdCandidate =
      process.env.EXPO_PUBLIC_EAS_PROJECT_ID ||
      (Constants.expoConfig?.extra as any)?.eas?.projectId ||
      (Constants as any)?.easConfig?.projectId;

    const tokenData = await Notifications.getExpoPushTokenAsync(
      isValidUuid(projectIdCandidate)
        ? { projectId: projectIdCandidate }
        : undefined
    );

    if (!isValidUuid(projectIdCandidate)) {
      console.warn('[FCM] Expo push token requested without a valid EAS projectId');
    }

    return tokenData.data;
  } catch (error) {
    // Handle specific error about missing VAPID key on web
    if (error instanceof Error && error.message.includes('vapidPublicKey')) {
      console.warn('[FCM] Push notifications not configured for web. To enable, add notification.vapidPublicKey to app.json');
      return null;
    }
    const message = error instanceof Error ? error.message : String(error);
    console.error('[FCM] Error getting push token', { message });
    return null;
  }
};

/**
 * Store FCM token for the current user
 */
export const storeFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        fcmToken: token,
        fcmTokenUpdatedAt: new Date(),
        platform: Platform.OS,
      },
      { merge: true }
    );
    console.log('[FCM] Token stored successfully');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[FCM] Error storing token', { message });
    throw error;
  }
};

/**
 * Remove FCM token for the current user (e.g., on logout)
 */
export const removeFCMToken = async (userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        fcmToken: null,
        fcmTokenUpdatedAt: null,
      },
      { merge: true }
    );
    console.log('[FCM] Token removed');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[FCM] Error removing token', { message });
    throw error;
  }
};

/**
 * Initialize FCM token for the current user
 * Call this when user logs in or app starts
 */
export const initializeFCMToken = async (userId: string): Promise<void> => {
  try {
    const token = await getPushToken();
    if (token) {
      await storeFCMToken(userId, token);
    } else {
      console.warn('[FCM] Could not get push token, skipping storage');
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[FCM] Error initializing token', { message });
  }
};

